/**
 * 반닫이 — 앞널 위 절반만 열리는 궤. 무쇠 장석의 배치가 곧 얼굴이다.
 */

import * as THREE from 'three';
import {
  bevelBox,
  
  
  
  
  roundedRect,
  type MeshWriter,
} from './kernel';
import { paintGeometry, type Piece } from './pieces';

// --- 공용 부품 ---

/** 무쇠 장석 — 반닫이·함의 얼굴. 판 + 못머리 */
function ironPlate(w: MeshWriter, width: number, height: number, nails = 4) {
  const p = bevelBox(width, height, 0.004, 0.001);
  w.add(p, 'iron', 0.3);
  for (let i = 0; i < nails; i++) {
    const a = (i / nails) * Math.PI * 2 + Math.PI / 4;
    const n = new THREE.SphereGeometry(0.005, 7, 5);
    n.translate((Math.cos(a) * width) / 2.8, (Math.sin(a) * height) / 2.8, 0.004);
    w.add(n, 'iron', 0.1);
  }
}

// --- 기물 ---

export const BANDAJI_PIECES: Piece[] = [

  {
    id: 'bandaji',
    name: '반닫이',
    collection: 'sahol',
    category: '수납',
    note: '앞널 위 절반만 앞으로 열린다. 무쇠 장석의 배치(경첩·앞바탕·들쇠·귀장식)가 곧 이 가구의 얼굴이다.',
    dims: { w: 0.92, d: 0.44, h: 0.76, legH: 0.09, plankT: 0.022, /** 속에 갠 이불·함. 0 이면 빈 궤 */ stocked: 1 },
    /* 위 절반 앞널이 가운데 선에서 앞으로 젖혀진다 — 경첩 y = legH + plankT + halfH(0.313) */
    openable: [{ part: '앞널', swing: -1.4, axis: 'x', pivot: [0, 0.425, -0.209], inside: { w: 0.8, h: 0.22, d: 0.36, at: [0, 0.55, 0] } }],   // 이불 더미(~0.43) 위
    build(w, d) {
      const bodyH = d.h - d.legH;
      w.part('몸통');
      for (const s of [-1, 1]) {
        const g = bevelBox(d.plankT, bodyH, d.d, 0.004);
        g.translate((s * (d.w - d.plankT)) / 2, d.legH + bodyH / 2, 0);
        w.add(g, 'oak', 0.6);
      }
      const back = bevelBox(d.w - d.plankT * 2, bodyH, d.plankT * 0.8, 0.003);
      back.translate(0, d.legH + bodyH / 2, d.d / 2 - d.plankT * 0.4);
      w.add(back, 'oak', 0.6);
      const top = bevelBox(d.w, d.plankT, d.d, 0.005);
      top.translate(0, d.h - d.plankT / 2, 0);
      w.add(top, 'oak', 0.7);
      const bottom = bevelBox(d.w - d.plankT * 2, d.plankT, d.d, 0.004);
      bottom.translate(0, d.legH + d.plankT / 2, 0);
      w.add(bottom, 'oak', 0.6);

      w.part('앞널');
      // 아래 절반은 고정, 위 절반이 여닫이문
      const halfH = (bodyH - d.plankT * 2) / 2;
      const lower = bevelBox(d.w - d.plankT * 2, halfH, d.plankT, 0.004);
      lower.translate(0, d.legH + d.plankT + halfH / 2, -d.d / 2 + d.plankT / 2);
      w.add(lower, 'oak', 0.6);
      w.movable('앞널', () => {
        const upper = bevelBox(d.w - d.plankT * 2, halfH, d.plankT, 0.004);
        upper.translate(0, d.legH + d.plankT + halfH * 1.5, -d.d / 2 + d.plankT / 2);
        w.add(upper, 'oak', 0.6);
      });

      /* **속** (2026-09-16, Chris: 「열면 속이 있어야」). 반닫이는 이불장이다 — 갠 이불·요가 층층이
       * 쌓여 아래 고정널 위로 머리를 내밀고, 그 위에 작은 함 하나. 위쪽은 비워 담는 곳(`inside`)이 산다 */
      if ((d.stocked ?? 1) > 0) {
        w.part('속');
        const iw = d.w - d.plankT * 2 - 0.03, idp = d.d - d.plankT * 2 - 0.02;
        let y = d.legH + d.plankT;
        const layers: [number, string, number][] = [[0.09, '#8f3b35', 0.98], [0.075, '#d9cfb8', 0.94], [0.085, '#4a5a7a', 0.96], [0.07, '#c7a56a', 0.9]];   // 더미 머리(0.43)가 아래 고정널(0.425) 위로 살짝 보인다
        for (const [lh, col, k] of layers) {
          const g = bevelBox(iw * k, lh, idp * k, 0.014, 2);
          g.translate((1 - k) * iw * 0.3, y + lh / 2, 0.005);
          paintGeometry(g, col);
          w.add(g, 'fabric', 0.6);
          y += lh;
        }
        const chest = bevelBox(0.24, 0.09, 0.16, 0.004);
        chest.rotateY(-0.08);
        chest.translate(iw * 0.22, y + 0.045, -0.03);
        w.add(chest, 'lacquer', 0.3);
      }

      w.part('장석');
      const zf = -d.d / 2 - 0.001;
      const midY = d.legH + d.plankT + halfH;
      // 경첩 — 여닫이 경계 좌우
      for (const s of [-1, 1]) {
        w.at(s * d.w * 0.3, midY, zf, () => ironPlate(w, 0.05, 0.09, 4));
      }
      // 앞바탕 + 들쇠 — 정중앙
      w.at(0, midY - halfH * 0.06, zf, () => {
        ironPlate(w, 0.13, 0.13, 6);
        const ring = new THREE.TorusGeometry(0.026, 0.005, 8, 18);
        ring.translate(0, -0.03, 0.006);
        w.add(ring, 'iron', 0.15);
      });
      // 귀장석 — 모서리를 감싼다
      for (const sx of [-1, 1]) {
        for (const sy of [d.legH + d.plankT + 0.03, d.h - d.plankT - 0.03]) {
          w.at(sx * (d.w / 2 - 0.045), sy, zf, () => ironPlate(w, 0.07, 0.05, 4));
        }
      }
      // 옆면 들손
      for (const sx of [-1, 1]) {
        const handle = new THREE.TorusGeometry(0.03, 0.005, 8, 22);
        handle.rotateY(Math.PI / 2);
        handle.translate(sx * (d.w / 2 - 0.012), d.legH + bodyH * 0.62, 0);
        w.add(handle, 'iron', 0.15);
      }

      w.part('굽');
      for (const sx of [-1, 1]) {
        const g = bevelBox(0.07, d.legH, d.d - 0.06, 0.005);
        g.translate((sx * (d.w - 0.09)) / 2, d.legH / 2, 0);
        w.add(g, 'oak', 0.4);
      }
      const rail = bevelBox(d.w - 0.18, d.legH * 0.42, 0.03, 0.004);
      rail.translate(0, d.legH * 0.75, -d.d / 2 + 0.02);
      w.add(rail, 'oak', 0.3);
    },
  },
];

export { roundedRect };
